import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

export interface AuditLogEntry {
  crm_audit_log_id: number;
  action_source_type: string;
  old_value: string | null;
  new_value: string | null;
  ip_address: string;
  created_datetime: string;
  tenant_id: number;
  crm_related_entity: {
    crm_related_enitity_id: number;
    display_name: string;
    key: string;
    related_entity_id?: number;
    title?: string;
  };
  action_user: {
    action_user_id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    avatar_url: string;
  } | null;
  action_type: {
    action_type_id: number;
    display_name: string;
    key: string;
  };
  action_by_api_client: {
    action_by_api_client_id: number;
    client_name: string;
    client_logo: string;
  } | null;
}

export interface PaginatedAuditLogResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: AuditLogEntry[];
}

export interface AuditLogTimelineEntry extends AuditLogEntry {
  related_entity_id?: number;
  action_type_id?: number;
  action_user_id?: number;
  action_by_api_client_id?: number | null;
}

export interface AuditLogTimelineResponse {
  create: AuditLogTimelineEntry[];
  update: AuditLogTimelineEntry[];
  delete: AuditLogTimelineEntry[];
}

export interface ActionType {
  action_type_id: number;
  action_type_key: string;
  action_type_display_name: string;
}

export interface ActionTypeResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ActionType[];
}

export const AuditLogService = {
  getActionTypes: async (): Promise<ActionTypeResponse> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");
      if (!tenantId) {
        throw new Error("Tenant ID not found in localStorage.");
      }

      const response = await axios.get(
        `${API_BASE_URL}/action-type/?tenant_id=${tenantId}`,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching action types:", error);
      return handleAuthError(error);
    }
  },

  getAuditLogs: async (params?: {
    page?: number;
    page_size?: number;
    action_type_id?: number;
    action_source_type?: string;
    created_datetime_gte?: string;
    created_datetime_lte?: string;
  }): Promise<PaginatedAuditLogResponse> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");
      if (!tenantId) {
        throw new Error("Tenant ID not found in localStorage.");
      }

      const queryParams: Record<string, any> = {
        ...params,
      };

      const response = await axios.get(
        `${API_BASE_URL}/crm-audit-log/?tenant_id=${tenantId}`,
        {
          headers: getAuthHeaders(),
          params: queryParams,
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      return handleAuthError(error);
    }
  },

  getAuditLogTimeline: async (
    auditLogId: number
  ): Promise<AuditLogTimelineResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/crm-audit-log/${auditLogId}/`,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching audit log timeline:", error);
      return handleAuthError(error);
    }
  },
};
